import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { SignOut } from "@phosphor-icons/react";

import CompanyCreateForm from "@/pages/company/CompanyCreateForm";
import InviteMemberForm from "@/pages/company/InviteMemberForm";
import MembersTable, { roleBadge } from "@/pages/company/MembersTable";

export default function Company() {
  const { refresh } = useAuth();
  const [data, setData] = useState({ company: null, role: null, members: [] });
  const [name, setName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/company").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const onError = (e) => toast.error(e.response?.data?.detail || "Failed");

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.post("/company", { name });
      toast.success("Company created — you're the manager");
      await refresh();
      load();
    } catch (e) { onError(e); } finally { setBusy(false); }
  };

  const invite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    try {
      await api.post("/company/invite", { email: inviteEmail, role: inviteRole });
      toast.success(`${inviteEmail} added as ${inviteRole}`);
      setInviteEmail("");
      load();
    } catch (e) { onError(e); }
  };

  const toggleMemberRole = async (m) => {
    const next = m.company_role === "manager" ? "member" : "manager";
    try {
      await api.patch(`/company/members/${m.id}`, { role: next });
      toast.success("Role updated");
      load();
    } catch (e) { onError(e); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.email} from the company?`)) return;
    try {
      await api.delete(`/company/members/${m.id}`);
      toast.success("Removed");
      load();
    } catch (e) { onError(e); }
  };

  const leave = async () => {
    if (!window.confirm("Leave the company?")) return;
    try {
      await api.post("/company/leave");
      toast.success("Left company");
      await refresh();
      load();
    } catch (e) { onError(e); }
  };

  if (!data.company) {
    return <CompanyCreateForm name={name} setName={setName} busy={busy} onSubmit={create} />;
  }

  const isManager = data.role === "manager";

  return (
    <div className="max-w-6xl">
      <div className="gf-overline">Company</div>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mt-2" data-testid="company-name">
        {data.company.name}
      </h1>
      <div className="text-zinc-600 text-sm mt-2">
        Your role: <span className={roleBadge(data.role)}>{data.role}</span>
        <span className="ml-3 font-mono text-xs text-zinc-500">
          {data.members.length} member{data.members.length === 1 ? "" : "s"}
        </span>
      </div>

      {isManager && (
        <InviteMemberForm
          inviteEmail={inviteEmail} setInviteEmail={setInviteEmail}
          inviteRole={inviteRole} setInviteRole={setInviteRole}
          onSubmit={invite}
        />
      )}

      <MembersTable
        members={data.members}
        isManager={isManager}
        onToggleRole={toggleMemberRole}
        onRemove={remove}
      />

      <Button
        onClick={leave}
        variant="outline"
        data-testid="leave-company"
        className="gf-sharp mt-6 border-zinc-300 uppercase font-bold tracking-wide text-red-700"
      >
        <SignOut size={16} className="mr-2" /> Leave company
      </Button>
    </div>
  );
}
